import { useState, useEffect, useCallback } from "react";
import { useSelector } from 'react-redux';

import translateApi from "../../api/translateApi";
import { getLanguages } from "../../slices/languageSlice";
import { getLabel, getLanguage } from "../../tools/Dictionary";
import { dataValidator } from "../../tools/dataValidator";
import ActionButton from "./ActionButton";
import AlertModal from "../Commons/AlertModal";

import "./LabelInput.css";
import { MdOutlineRemoveRedEye, MdMode, MdOutlineAutorenew, MdGTranslate } from "react-icons/md";

const LabelInput = (props) => {

  //Props
  const ReadOnly = props.ReadOnly;
  const Data = props.Data;
  const Property = props.Property;
  const ListValidation = props.ListValidation;
  const OnChangeValue = props.OnChangeValue;
  const TextStyle = props.TextStyle;
  const PreviewStyle = props.PreviewStyle;
  const PreviewMaxLength = props.PreviewMaxLength;
  const Loading = props.Loading;
  const Autofill = props.Autofill;

  //Redux
  const Language = useSelector(getLanguages);

  //State
  const [form, setForm] = useState({});
  const [error, setError] = useState({});
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(false);
  const [alert, setAlert] = useState({
    Title: "",
    Message: "",
    Confirm: "",
    onConfirm: () => void 0,
    Display: false
  });

  const loadData = useCallback((_data) =>
  {
    setForm(_data);
  }, []);


  useEffect(() => {
    loadData(Data);
  }, [Data, loadData]);

  const onChangeValue = (e, _language) => {

    let tempForm = {...form};

    tempForm[_language] = e.target.value;

    onValidateField(_language, e.target.value);

    setForm(tempForm);

  };

  const onClickAutofill = () =>
  {

    let tempForm = { ...form };

    tempForm["EN"] = Autofill.value;

    setForm(tempForm);

    OnChangeValue(tempForm, Property);

  };

  // Validate field
  const onValidateField = (field = "", value = "") =>
  {
    let currentError = { ...error }; // Create a copy of the error object

    let Validation = []; // Array to store validation results
    let currentValidation = [];
    let currentField = "";

    let isValid = true; // Flag to track overall validity

    Language.forEach(l => {
      currentField = l.code;
      if (field == "" || field == currentField)
      {
        let currentValue = field ? value : form[currentField];

        // Validate
        currentValidation = dataValidator([
          { field: currentField, value: currentValue, listValidation: ListValidation }
        ]);

        Validation = [...Validation, ...currentValidation];

      }
    });



    Validation.forEach(validation =>
    {
      // Iterate over each validation result
      if (validation.valid)
      {
        currentError[validation.field] = ""; // If valid, clear the corresponding error field
      } else
      {
        currentError[validation.field] = validation.reason; // If invalid, set the error reason in the corresponding field
        isValid = false; // Set isValid to false if any validation fails
      }
    });

    setError(currentError); // Update the error state
    
    return isValid; // Return the overall validity flag
  };

  const onClickSave = () => {

    if (!onValidateField()) return;


    OnChangeValue(form, Property);
    setModal(false);

  };

  const onCloseModal = () => {

    setModal(false);
    setError({});
    loadData(Data);

  };


  const displayPreview = () => {

    let value = form[getLanguage().code] || Data["EN"] || "";

    if (!value || value == "")  return value;

    if (!PreviewMaxLength) return value;

    if (value.length <= PreviewMaxLength) return value;

    return value.substring(0, PreviewMaxLength) + " [...]";

  };

  const onClickTranslate = async(_language, _text) => {

    try{


      if ((_text == "" || !_text) && _text != "0") return;

      setLoading(true);

      let targetLanguage = Language.filter(l => l.code != _language).map(l => l.code);

      const params = {
        sourceLanguage: _language,
        targetLanguage: targetLanguage,
        text: _text
      };

      const translateResponse = await translateApi.translateText(params);


      if (!translateResponse.Success)
      {
        let error = new Error()
        error.message = translateResponse.Error && translateResponse.Error.component ? getLabel(translateResponse.Error.component, translateResponse.Error.label) : undefined;

        throw error;
      }

      let tempForm = { ...form };

      translateResponse.Data.forEach(t => {

        tempForm[t.language] = t.text;

      });

      onValidateField();

      setForm(tempForm);

      setLoading(false);

    } catch (err)
    {


      setLoading(false);


      setAlert({
        Title: getLabel("App", "Error"),
        Message: err.message || getLabel("App", "Sorry, an unknown error has occurred. Please try again later or contact our support team for assistance."),
        Confirm: getLabel("App", "Ok"),
        onConfirm: () => setAlert(alert => ({ ...alert, Display: false })),
        Display: true
      });

    };

  }



  const displayModal = () => {

    if (!modal) return null;

    return (
      <div className="page_modal_background">
        <AlertModal
          Title={alert.Title}
          Message={alert.Message}
          Confirm={alert.Confirm}
          onConfirm={alert.onConfirm}
          Display={alert.Display}
        />
        <div className="page_modal_cont" style={{ width: "900px" }}>
          <div className="page_modal_header_cont" style={{ marginBottom: "10px" }}>
            {getLabel("LabelInput", (ReadOnly ? "Display" : "Edit"))}
          </div>
          <div className="page_modal_body_cont" style={{padding: "10px"}}>
            <div className="labelinput_cont">
              {Language.map((l, index) => (
                <div className="labelinput_col" key={index}>
                  <div className="labelinput_lang page_noselect" style={{width: "200px", marginBottom: "5px"}} title={l.name}>
                    {l.name}
                    {index != -1 &&
                      <ActionButton
                        IsDesktop={false}
                        Label={getLabel("LabelInput", "Instant Translation")}
                        Icon={MdGTranslate}
                        Disabled={loading}
                        OnClick={(loading) ? undefined : () => onClickTranslate(l.code, form[l.code])}
                        buttonStyle={{ width: "36px", height: "36px", marginLeft: "10px" }}
                      />
                    }
                  </div>
                  <textarea
                    className={"page_textarea_input " + (error[l.code] ? "page_modal_body_col_input_error" : "")}
                    rows={3}
                    value={form[l.code] || ""}
                    onChange={(e) => onChangeValue(e, l.code)}
                    type="text"
                    style={TextStyle}
                    disabled={ReadOnly || Loading}
                  />
                  {error[l.code] &&
                    <div className="page_modal_body_col_extra page_modal_body_col_error">
                      {error[l.code]}
                    </div>
                  }
                </div>
              ))}
            </div>
          </div>
          <div className="page_modal_footer_cont">
            <input
              type="button"
              className="page_button page_modal_footer_button"
              value={getLabel("App", "Close")}
              style={{ backgroundColor: "grey", maxWidth: "200px" }}
              onClick={onCloseModal}
              disabled={Loading}
            />
            {!ReadOnly &&
            <input
              type="button"
              className="page_button page_modal_footer_button"
              value={getLabel("App", "Confirm")}
              style={{ maxWidth: "200px" }}
              onClick={onClickSave}
              disabled={Loading}
            />
            }
          </div>
        </div>
      </div>
    );

  };

  if (!Data || !form) return null;



  return (
    <div className="labelinput_cont">
      {displayModal()}
      <div className="labelinput_preview_cont">
        <div className="labelinput_read_text" style={PreviewStyle}>
          {displayPreview()}   
        </div>
        <div className="labelinput_button_row">
          <div className="page_button"
            style={{ width: "180px", height: "30px", fontSize: "20px", marginRight: "5px" }}
            title={getLabel("LabelInput", (ReadOnly ? "Display" : "Edit"))}
            onClick={() => setModal(true)}
          >
            <div className="labelinput_preview_col" style={{ width: "30%" }}>
              {ReadOnly ? <MdOutlineRemoveRedEye /> : <MdMode />}
            </div>
            <div className="labelinput_preview_col" style={{ width: "70%" }}>
              {getLabel("LabelInput", (ReadOnly ? "Display" : "Edit"))}
            </div>
          </div>
          {Autofill &&
          <div className="page_button"
            style={{ width: "40px", height: "30px", fontSize: "20px", marginRight: "5px" }}
            title={getLabel("LabelInput", "Autofill with value of field") + `'${Autofill.property}'`}
              onClick={onClickAutofill}
          >
            <div className="labelinput_preview_col" style={{fontSize: "20px" }}>
              <MdOutlineAutorenew />
            </div>
          </div>
          }
        </div>
        
      </div>
    </div>
    );


}

export default LabelInput;